'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  moveDate: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  serviceType?: string;
  moveDate?: string;
  message?: string;
}

interface ContactFormProps {
  className?: string;
}

const ContactForm = ({ className = '' }: ContactFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    moveDate: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const serviceTypes = [
    { value: '', label: 'Select Service Type' },
    { value: 'residential', label: 'Residential Moving' },
    { value: 'commercial', label: 'Commercial Relocation' },
    { value: 'packing', label: 'Packing Services' },
    { value: 'assembly', label: 'Assembly & Installation' },
    { value: 'storage', label: 'Storage Solutions' },
    { value: 'other', label: 'Other Services' }
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^[0-9]{8}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 8-digit Kuwait phone number';
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    if (!formData.moveDate) {
      newErrors.moveDate = 'Preferred move date is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Form submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: '',
        moveDate: '',
        message: ''
      });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-card rounded-lg shadow-soft p-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-border rounded w-3/4"></div>
              <div className="h-4 bg-border rounded w-full"></div>
              <div className="space-y-4">
                <div className="h-12 bg-border rounded"></div>
                <div className="h-12 bg-border rounded"></div>
                <div className="h-12 bg-border rounded"></div>
                <div className="h-32 bg-border rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Send Us a <span className="text-secondary">Message</span>
            </h2>
            <p className="text-lg text-text-secondary font-body">
              Fill out the form below and we'll get back to you within 2 hours during business hours.
            </p>
          </div>

          <div className="bg-card rounded-lg shadow-soft p-8 lg:p-12">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0" />
                <div>
                  <h4 className="font-body font-semibold text-success mb-1">Message Sent Successfully!</h4>
                  <p className="text-sm text-text-secondary">Thank you for contacting Almanara Moving. We'll respond to your inquiry within 2 hours.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start space-x-3">
                <Icon name="ExclamationCircleIcon" size={24} variant="solid" className="text-error flex-shrink-0" />
                <div>
                  <h4 className="font-body font-semibold text-error mb-1">Submission Failed</h4>
                  <p className="text-sm text-text-secondary">There was an error sending your message. Please try again or contact us directly.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-body font-semibold text-primary mb-2">
                    Full Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.name ? 'border-error' : 'border-input'
                    } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error flex items-center space-x-1">
                      <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                      <span>{errors.name}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-body font-semibold text-primary mb-2">
                    Email Address <span className="text-error">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.email ? 'border-error' : 'border-input'
                    } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error flex items-center space-x-1">
                      <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                      <span>{errors.email}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-body font-semibold text-primary mb-2">
                    Phone Number <span className="text-error">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.phone ? 'border-error' : 'border-input'
                    } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body`}
                    placeholder="69911181"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-error flex items-center space-x-1">
                      <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                      <span>{errors.phone}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-body font-semibold text-primary mb-2">
                    Service Type <span className="text-error">*</span>
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.serviceType ? 'border-error' : 'border-input'
                    } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body`}
                  >
                    {serviceTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.serviceType && (
                    <p className="mt-1 text-sm text-error flex items-center space-x-1">
                      <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                      <span>{errors.serviceType}</span>
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="moveDate" className="block text-sm font-body font-semibold text-primary mb-2">
                  Preferred Move Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  id="moveDate"
                  name="moveDate"
                  value={formData.moveDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.moveDate ? 'border-error' : 'border-input'
                  } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body`}
                />
                {errors.moveDate && (
                  <p className="mt-1 text-sm text-error flex items-center space-x-1">
                    <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                    <span>{errors.moveDate}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-body font-semibold text-primary mb-2">
                  Message <span className="text-error">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.message ? 'border-error' : 'border-input'
                  } focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-base font-body resize-none`}
                  placeholder="Tell us about your moving needs, special requirements, or any questions you have..."
                ></textarea>
                {errors.message && (
                  <p className="mt-1 text-sm text-error flex items-center space-x-1">
                    <Icon name="ExclamationCircleIcon" size={14} variant="solid" />
                    <span>{errors.message}</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center space-x-3 bg-brand-purple text-white px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} variant="solid" className="animate-spin" />
                    <span>Sending Message...</span>
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                    <span>Send Message</span>
                  </>
                )}
              </button>

              <p className="text-sm text-text-secondary text-center font-body">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;